import React, { useEffect, useState, useMemo } from 'react';
import { ActivityIndicator, Platform, View, Image } from 'react-native';
import styled from 'styled-components/native';
import Colors from '../../../constants/Colors';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import Button from '../../../components/Button';
import IconButton from '../../../components/IconButton';
import { useUploadFiles } from '../../hooks/File.hooks';
import * as DocumentPicker from 'expo-document-picker';
import { P } from '../../../components/Typography';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import FormElementQuestion from './components/FormElementQuestion';
import Spacer from '../../../components/Spacer';
import ImageActionSheet, {
  getFileFromUri
} from '../../../components/modals/ImageActionSheet';
import * as ImagePicker from 'expo-image-picker';
import api from '../../../api/api';
import { useUserContext } from '../../../context/UserContext';
import { log } from '../../../tools/LogHelper';
import { stringify } from 'flatted';
import accessibilityTags from '../../../../accessibilityTags';

const StyledIcon = styled(MaterialCommunityIcons)`
  font-size: 42px;
  position: absolute;
  opacity: 0.22;
  z-index: -1;
`;

const StyledDragArea = styled.View<{ highlight: boolean; empty: boolean }>`
  background-color: ${Colors.background};
  position: relative;
  height: ${p => (p.empty ? '200px' : undefined)};
  border: solid 1px ${p => (p.highlight ? '#000' : '#333')};
  border-radius: 4px;
`;

const StyledUploadedFileArea = styled.View`
  flex-direction: row;
  padding: 10px 14px 0 14px;
`;

const StyledFileItemImageView = styled.View`
  width: 50px;
  height: 50px;
  align-self: center;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: #f6f5fc;
  border: 2px solid #cfcaf6;
  border-radius: 8px;
  margin-right: 8px;
`;

const StyledFileItemImage = styled.Image`
  width: 100%;
  height: 100%;
  border-radius: 6px;
`;

const StyledUploadedFileTextArea = styled.View`
  flex: 3;
  align-items: flex-start;
`;

const StyledUploadedFileButtonArea = styled.View`
  flex: 1;
  align-items: flex-end;
`;

const StyledUploadedFileName = styled(P)`
  height: 22px;
  font-size: 16px;
  color: ${Colors.primaryPurple};
`;

const StyledUploadedFileSize = styled(P)`
  font-size: 13px;
  color: black;
`;

const StyledPlaceHolderView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledPlaceHolderText = styled(P)`
  font-size: 24px;
  margin-bottom: 10px;
`;

const StyledFileArea = styled.View`
  background-color: ${Colors.background};
  position: relative;
  height: 200px;
  align-items: center;
  justify-content: center;
  border: solid 1px #ccc;
  border-radius: 4px;
`;

const StyledButton = styled(Button)`
  max-width: 270px;
  align-self: center;
  border-radius: 6px;
`;

const StyledFilesContainer = styled.View`
  background-color: ${Colors.background};
  align-items: center;
  justify-content: center;
  border: solid 1px #ccc;
  border-radius: 4px;
  padding: 0 0 10px 0;
`;

const StyledFileContainer = styled.View`
  width: 100%;
`;

const StyledFileDivider = styled.View`
  height: 1px;
  background-color: #ccc;
  margin-top: 10px;
`;

const StyledButtonSeparatorText = styled(P)`
  font-size: 18px;
  margin-bottom: 10px;
`;

const StyledMultipleFilesButtonContainer = styled.View`
  flex-direction: row;
  width: 100%;
  justify-content: center;
`;

const StyledImageUploadArea = styled.TouchableOpacity`
  aspect-ratio: 1;
  width: 100%;
  max-width: 343px;
  max-height: 343px;
  align-items: center;
  justify-content: center;
  align-self: center;
  background-color: #f6f5fc;
  border: 2px solid #cfcaf6;
  border-radius: 24px;
  overflow: hidden;
`;

const StyledImagePreview = styled(Image)`
  width: 100%;
  height: 100%;
  border-radius: 22px;
`;

const StyledLoadingImageCover = styled.View<{ borderRadius?: number }>`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: #f6f5fc;
  border-radius: ${p => (p.borderRadius ? p.borderRadius : 22)}px;
`;

const StyledCloseIconButton = styled(IconButton)`
  position: absolute;
  right: 8px;
  top: 8px;
  height: 42px;
  width: 42px;
  z-index: 3;
`;

enum FileInputType {
  All,
  Image,
  Video,
  Others
}

export interface FormFileValue {
  url?: string; // the destination url returned from the presigned url
  name?: string | null; // name of the original file
  size?: number; // the file size
}

export type FormFileConfig = {
  multiple: boolean;
  uploadPath?: string;
  inputType?: string;
};

const IMAGE_RELOAD_ATTEMPTS = 10;

const FormFile = <T extends object>({
  element,
  disabled
}: FormElementProps<T, FormFileConfig>) => {
  const { formValues, setFormValue, setDisableEventTransition } =
    useDynamicFormContext<T, FormFileConfig>();
  const { multiple, inputType } = element.config || {};
  const key = (element.dataSource as keyof T) || element.key;
  const [dragover, setDragover] = useState(false);
  const { uploadFiles, upload, remove, removeAll, error, isUploading } =
    useUploadFiles('form-files', 'forms/presign');
  const { accessToken } = useUserContext();
  const uploadedFiles = useMemo(
    () => (formValues[key] || []) as FormFileValue[],
    [formValues, key]
  );

  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | undefined>(
    uploadedFiles ? uploadedFiles[0]?.url : undefined
  );

  const [loadedImageUrls, setLoadedImageUrls] = useState<string[]>([]);
  const [loadingImagePreview, setLoadingImagePreview] =
    useState<boolean>(false);
  const [reloadCount, setReloadCount] = useState<number>(0);

  const [openImageActionSheet, setOpenImageActionSheet] = useState(false);

  const fileInputType = useMemo(() => {
    let fileInputType = FileInputType.All;
    if (inputType?.includes('.jpg')) {
      fileInputType = FileInputType.Image;
    } else if (inputType?.includes('.mp4')) {
      fileInputType = FileInputType.Video;
    } else if (inputType?.includes('.pdf') || inputType?.includes('.doc')) {
      fileInputType = FileInputType.Others;
    }
    return fileInputType;
  }, [inputType]);

  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>();

  function getMimeType(fileExtension: string): string {
    switch (fileExtension.toLowerCase()) {
      case '.jpeg':
      case '.jpg':
        return 'image/jpeg';
      case '.png':
        return 'image/png';
      case '.heic':
        return 'image/heic';
      case '.gif':
        return 'image/gif';
      case '.mp4':
        return 'video/mp4';
      case '.m4v':
        return 'video/x-m4v';
      case '.mov':
        return 'video/quicktime';
      case '.pdf':
        return 'application/pdf';
      case '.doc':
        return 'application/msword';
      case '.docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case '.ppt':
        return 'application/vnd.ms-powerpoint';
      case '.pptx':
        return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case '*':
        return '*/*';
    }
    return '';
  }

  const reloadImage = () => {
    if (reloadCount < IMAGE_RELOAD_ATTEMPTS) {
      setTimeout(() => {
        setReloadCount(reloadCount + 1);
        setUploadedImageUrl(
          uploadedImageUrl?.includes('reload')
            ? `${uploadedImageUrl.split('reload')[0]}reload=${reloadCount}`
            : `${uploadedImageUrl}&reload=${reloadCount}`
        );
      }, 1000);
    } else {
      setReloadCount(reloadCount + 1);
      setLoadingImagePreview(false);
    }
  };

  const removeFile = (formFile: FormFileValue) => {
    if (!uploadedFiles?.length) return;
    const remainingFormFiles = [...uploadedFiles]?.filter(
      (uploadedFile: FormFileValue) => uploadedFile.url !== formFile.url
    );
    setFormValue(key, remainingFormFiles as any, element);
    if (multiple) {
      if (fileInputType === FileInputType.Image) {
        setSelectedImageFiles([
          ...selectedImageFiles.filter(e => e.name !== formFile.name)
        ]);
        setLoadedImageUrls(
          loadedImageUrls.filter(e =>
            formFile.url ? !e.includes(formFile.url) : false
          )
        );
      }
      const uploadFileToRemove = uploadFiles?.find(
        uploadFile =>
          uploadFile.presignedResult?.destinationUrl === formFile.url
      )?.file;

      if (uploadFileToRemove) remove(uploadFileToRemove);
    } else {
      if (fileInputType === FileInputType.Image) {
        setLoadedImageUrls([]);
        setSelectedImageFiles([]);
        setReloadCount(11);
      }
      removeAll();
    }
  };

  const blobToBase64 = (blob: Blob) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise(resolve => {
      reader.onloadend = () => {
        resolve(reader.result);
      };
    });
  };

  // loads image into img element using id
  const loadToWebImagePreview = async (id: string, url: string) => {
    if (url && Platform.OS === 'web') {
      const img: HTMLImageElement | null = document.getElementById(
        id
      ) as HTMLImageElement;
      const blob = await urlToBlob(url);
      img.src = String(await blobToBase64(blob));
    }
  };

  useEffect(() => {
    if (
      uploadedFiles &&
      multiple &&
      Platform.OS === 'web' &&
      fileInputType === FileInputType.Image
    ) {
      uploadedFiles.forEach(file => {
        if (
          file.name &&
          file.url &&
          getMimeType('.' + file.name.split('.')[1]).includes('image')
        ) {
          if (
            !loadedImageUrls.find(e =>
              file.url ? e.includes(file.url) : false
            )
          ) {
            setLoadingImagePreview(true);
            loadToWebImagePreview(file.name, file.url);
          }
        }
      });
    }
  }, [uploadedFiles, uploadedImageUrl]);

  const [selectedImageFiles, setSelectedImageFiles] = useState<FormFileValue[]>(
    (formValues[key] || []) as FormFileValue[]
  );
  const [initialized, setInitialized] = useState<boolean>(false);

  // initialize mobile image preview
  useEffect(() => {
    if (
      selectedImageFiles.length === 0 &&
      uploadedFiles.length !== 0 &&
      !initialized &&
      fileInputType === FileInputType.Image
    ) {
      setSelectedImageFiles([...uploadedFiles]);
      setInitialized(true);
    }
  }, [uploadedFiles]);

  const uploadedFileList = (
    Platform.OS === 'web' || fileInputType !== FileInputType.Image
      ? uploadedFiles
      : selectedImageFiles
  )?.map((uploadedFile: FormFileValue, index: number) => {
    const uploadFileIndex = uploadFiles
      ? uploadFiles.findIndex(uFile => uFile.file.name === uploadedFile.name)
      : -1;

    const loadedURL = (urls: string[]) =>
      urls.find(e => (uploadedFile.url ? e.includes(uploadedFile.url) : false));

    return (
      <StyledFileContainer key={index}>
        <StyledUploadedFileArea>
          {fileInputType === FileInputType.Image ? (
            Platform.OS === 'web' ? (
              <StyledFileItemImageView>
                {(isUploading || loadingImagePreview) &&
                  !loadedURL(loadedImageUrls) && (
                    <ActivityIndicator
                      style={{ position: 'absolute', zIndex: 2 }}
                      size={12}
                    />
                  )}
                {loadingImagePreview && !loadedURL(loadedImageUrls) && (
                  <StyledLoadingImageCover borderRadius={6} />
                )}
                <img
                  id={`${uploadedFile.name}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 6,
                    objectFit: 'cover'
                  }}
                  src={undefined}
                  onLoad={() => {
                    if (uploadedFile.url) {
                      setLoadedImageUrls([
                        ...loadedImageUrls.filter(e =>
                          uploadedFile.url
                            ? !e.includes(uploadedFile.url)
                            : false
                        ),
                        uploadedFile.url
                      ]);
                    }
                    setLoadingImagePreview(false);
                  }}
                  alt=''
                  onError={reloadImage}
                />
              </StyledFileItemImageView>
            ) : (
              <StyledFileItemImageView>
                {(isUploadingFiles || loadingImagePreview) && (
                  <ActivityIndicator
                    style={{ position: 'absolute' }}
                    size={16}
                  />
                )}
                <StyledFileItemImage
                  key={String(key) + index}
                  resizeMode='cover'
                  source={{
                    uri: uploadedFile.url,
                    headers: {
                      [api.environment.accessTokenKey]: `Bearer ${accessToken}`
                    }
                  }}
                  onLoadStart={() => {
                    setLoadingImagePreview(true);
                  }}
                  onLoad={() => {
                    setLoadingImagePreview(false);
                  }}
                />
              </StyledFileItemImageView>
            )
          ) : (
            <></>
          )}
          <StyledUploadedFileTextArea>
            <StyledUploadedFileName>{uploadedFile.name}</StyledUploadedFileName>
            <StyledUploadedFileSize>
              {((uploadedFile?.size || 0) / 1000).toLocaleString(undefined, {
                maximumFractionDigits: 2
              })}{' '}
              KB
            </StyledUploadedFileSize>
          </StyledUploadedFileTextArea>
          <StyledUploadedFileButtonArea>
            <View style={{ alignItems: 'center' }}>
              <IconButton
                accessibilityLabel={accessibilityTags.uploadImageButtonID}
                testID={accessibilityTags.uploadImageButtonID}
                size='small'
                onPress={() => {
                  removeFile(uploadedFiles[index]);
                }}
              >
                <Ionicons name='close' size={18} color='black' />
              </IconButton>
              {uploadFileIndex > -1 &&
                uploadFiles &&
                uploadFiles?.[uploadFileIndex]?.progress !== 1 && (
                  <ActivityIndicator size={16} />
                )}
            </View>
          </StyledUploadedFileButtonArea>
        </StyledUploadedFileArea>
        {multiple && (
          <StyledFileDivider
            style={{ height: 1, backgroundColor: '#ccc', marginTop: 10 }}
          />
        )}
      </StyledFileContainer>
    );
  });

  useEffect(() => {
    const uploadProgress = uploadFiles?.map(
      uploadFile => uploadFile.progress === 1
    );
    const allUploadCompleted = uploadProgress?.every(Boolean);
    setIsUploadingFiles(uploadFiles && !allUploadCompleted);
    if (allUploadCompleted) {
      if (
        uploadFiles &&
        uploadFiles.length > 0 &&
        getMimeType(
          '.' + uploadFiles[uploadFiles?.length - 1].file.name.split('.')[1]
        ).includes('image')
      ) {
        setUploadedImageUrl(
          uploadFiles[uploadFiles?.length - 1].presignedResult?.destinationUrl
        );
      }
      setDisableEventTransition(false);
    }

    if (uploadFiles?.length) {
      let localFormFiles: FormFileValue[] = [];
      if (multiple) {
        localFormFiles = uploadedFiles;
        for (let i = 0; i < uploadFiles.length; i++) {
          const { file, presignedResult } = uploadFiles[i];
          const formFile: FormFileValue = {
            name: file.name,
            size: file.size,
            url: presignedResult?.destinationUrl
          };

          const index = localFormFiles.findIndex(
            (localFormFile: FormFileValue) =>
              localFormFile.name === formFile.name &&
              localFormFile.size === formFile.size
          );
          if (index <= -1) {
            localFormFiles.push(formFile);
          } else {
            localFormFiles[index].url = formFile.url;
          }
        }
      } else {
        const latestUploadFileIndex = uploadFiles.length - 1;
        const { file, presignedResult } = uploadFiles[latestUploadFileIndex];
        if (file) {
          const formFile: FormFileValue = {
            name: file.name,
            size: file.size,
            url: presignedResult?.destinationUrl
          };
          localFormFiles.push(formFile);
        }
      }
      setFormValue(key, localFormFiles as any, element);
    }
  }, [uploadFiles]);

  const urlToBlob = async (url: string) => {
    return new Promise<Blob>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.onerror = reject;
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          resolve(xhr.response);
        }
      };
      xhr.open('GET', url);
      xhr.setRequestHeader(
        api.environment.accessTokenKey,
        `Bearer ${accessToken}`
      );
      xhr.responseType = 'blob';
      xhr.send();
    });
  };

  // set Image if there is a previously recorded one
  useEffect(() => {
    if (
      !uploadedImageUrl &&
      fileInputType === FileInputType.Image &&
      !multiple &&
      uploadedFiles[0]?.url &&
      formValues[key]
    ) {
      setUploadedImageUrl(uploadedFiles[0].url);
    }
  }, [uploadedFiles]);

  // set image on web img element
  useEffect(() => {
    if (
      uploadedImageUrl &&
      Platform.OS === 'web' &&
      fileInputType === FileInputType.Image &&
      !multiple
    ) {
      loadToWebImagePreview(`${String(key)}-preview`, uploadedImageUrl);
    }
  }, [uploadedImageUrl]);

  const sendFiles = async (files: File[]) => {
    const distinctFiles = files.filter(
      file =>
        !uploadedFiles.some(
          uFile => uFile.name === file.name && uFile.size === file.size
        )
    );
    setDisableEventTransition(true);
    if (fileInputType === FileInputType.Image && Platform.OS === 'web') {
      setReloadCount(0);
      setLoadingImagePreview(true);
    }
    if (multiple) {
      upload(distinctFiles);
    } else {
      upload([distinctFiles[0]]);
    }
  };

  const selectFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        multiple: multiple,
        type: inputType
          ? inputType?.split(', ')?.flatMap((item: string) => getMimeType(item))
          : '*/*'
      });
      if (!result.canceled) {
        await handleFileResult(result);
      }
    } catch (error) {
      log('select file error:', stringify(error));
    }
  };

  const handleFileResult = async (
    result: DocumentPicker.DocumentPickerResult
  ) => {
    if (result.canceled || result.assets.length <= 0) return;
    const files: File[] = [];
    for (const asset of result.assets) {
      const file = await getFileFromUri(
        asset.uri,
        asset.name,
        asset.mimeType || '*/*'
      );
      files.push(file);
    }
    sendFiles(files);
  };

  const handleImageResult = async (result: ImagePicker.ImagePickerResult) => {
    log('handleImageResult', stringify(result));
    if (result.canceled || result.assets.length <= 0) return;
    const files: File[] = [];
    const selected: FormFileValue[] = [];
    for (const asset of result.assets) {
      let fileName = asset.fileName;
      if (!fileName) {
        const split = asset.uri?.split('/');
        fileName = split[split.length - 1];
      }
      const formFile = {
        name: fileName,
        size: asset.fileSize,
        url: asset.uri
      };
      const file = await getFileFromUri(
        asset.uri,
        fileName,
        getMimeType('.' + fileName.split('.')[1])
      );
      files.push(file);
      selected.push(formFile);
    }

    if (multiple) {
      setSelectedImageFiles([...selectedImageFiles, ...selected]);
    } else {
      setSelectedImageFiles([
        {
          name: selected[0].name,
          size: selected[0].size,
          url: selected[0].url
        }
      ]);
    }
    sendFiles(files);
  };

  useEffect(() => {
    if (!multiple && uploadedFiles && uploadedFiles.length > 1) {
      setFormValue(key, [uploadedFiles[0]] as any, element);
    }
  }, [element, key, multiple, setFormValue, uploadedFiles]);

  return (
    <View>
      <FormElementQuestion element={element} />
      {Platform.OS === 'web' ? (
        fileInputType === FileInputType.Image && !multiple ? (
          <StyledImageUploadArea
            disabled={isUploading || loadingImagePreview}
            onPress={() => {
              document.getElementById(String(key))?.click();
            }}
          >
            <StyledIcon name='camera-plus' />
            {loadingImagePreview && (
              <ActivityIndicator style={{ position: 'absolute', zIndex: 2 }} />
            )}
            {loadingImagePreview && <StyledLoadingImageCover />}
            {uploadedFiles.length > 0 && (
              <StyledCloseIconButton
                size='small'
                onPress={() => {
                  removeFile(uploadedFiles[0]);
                }}
              >
                <Ionicons name='close' size={18} color='black' />
              </StyledCloseIconButton>
            )}
            <div
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onDragOver={event => {
                event.preventDefault();
                if (disabled) return;
                setDragover(true);
              }}
              onDragLeave={() => {
                !disabled && setDragover(false);
              }}
              onDrop={event => {
                if (!isUploading && !loadingImagePreview) {
                  event.preventDefault();
                  if (disabled) return;
                  setDragover(false);
                  const filesResult = [];
                  if (event.dataTransfer.files) {
                    for (let i = 0; i < event.dataTransfer.files.length; i++) {
                      filesResult.push(event.dataTransfer.files[i]);
                    }
                  }
                  setLoadingImagePreview(true);
                  sendFiles(filesResult);
                }
              }}
            >
              {reloadCount < IMAGE_RELOAD_ATTEMPTS + 1 && (
                <img
                  id={`${String(key)}-preview`}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 22,
                    objectFit: 'cover'
                  }}
                  src={undefined}
                  onLoad={() => {
                    setLoadingImagePreview(false);
                  }}
                  onError={reloadImage}
                />
              )}
              <input
                id={String(key)}
                accept='image/*'
                type='file'
                multiple={false}
                style={{ display: 'none' }}
                onChange={event => {
                  const filesResult = [];
                  if (event.target.files) {
                    for (let i = 0; i < event.target.files.length; i++) {
                      filesResult.push(event.target.files[i]);
                    }
                    if (filesResult.length) {
                      setLoadingImagePreview(true);
                      sendFiles(filesResult);
                    }
                  }
                }}
              />
            </div>
          </StyledImageUploadArea>
        ) : (
          <div>
            <StyledDragArea
              highlight={dragover}
              empty={!formValues[key] || (formValues[key] as any).length === 0}
            >
              <div
                style={{ flex: 1 }}
                onDragOver={event => {
                  event.preventDefault();
                  if (disabled) return;
                  setDragover(true);
                }}
                onDragLeave={() => {
                  !disabled && setDragover(false);
                }}
                onDrop={event => {
                  event.preventDefault();
                  if (disabled) return;
                  setDragover(false);
                  const filesResult = [];
                  if (event.dataTransfer.files) {
                    for (let i = 0; i < event.dataTransfer.files.length; i++) {
                      filesResult.push(event.dataTransfer.files[i]);
                    }
                  }
                  sendFiles(filesResult);
                }}
              >
                {(!formValues[key] ||
                  (formValues[key] as any).length === 0) && (
                  <StyledPlaceHolderView>
                    <StyledPlaceHolderText color='dark'>
                      Drag a file here
                    </StyledPlaceHolderText>
                    <StyledPlaceHolderText color='dark'>
                      -- or --
                    </StyledPlaceHolderText>
                    <StyledButton
                      outlined
                      onPress={() => {
                        document.getElementById(String(key))?.click();
                      }}
                    >
                      Select File
                    </StyledButton>
                  </StyledPlaceHolderView>
                )}
                <input
                  id={String(key)}
                  accept='*/*'
                  type='file'
                  multiple={multiple}
                  style={{ display: 'none' }}
                  onChange={event => {
                    const filesResult = [];
                    if (event.target.files) {
                      for (let i = 0; i < event.target.files.length; i++) {
                        filesResult.push(event.target.files[i]);
                      }
                      sendFiles(filesResult);
                    }
                  }}
                />
                <div>{uploadedFileList}</div>
                {formValues[key] &&
                  (formValues[key] as any).length !== 0 &&
                  multiple && (
                    <View style={{ alignItems: 'center' }}>
                      <Spacer />
                      <StyledButton
                        outlined
                        size='small'
                        disabled={isUploadingFiles}
                        onPress={selectFile}
                      >
                        + Add File
                      </StyledButton>
                      <Spacer />
                    </View>
                  )}
              </div>
            </StyledDragArea>
          </div>
        )
      ) : (
        <>
          {fileInputType === FileInputType.Image && !multiple ? (
            <StyledImageUploadArea
              disabled={isUploadingFiles || loadingImagePreview}
              onPress={() => {
                setOpenImageActionSheet(true);
              }}
            >
              <StyledIcon name='camera-plus' />
              {uploadedFiles.length > 0 && (
                <StyledCloseIconButton
                  size='small'
                  onPress={() => {
                    removeFile(uploadedFiles[0]);
                  }}
                >
                  <Ionicons name='close' size={18} color='black' />
                </StyledCloseIconButton>
              )}

              {(isUploadingFiles || loadingImagePreview) && (
                <ActivityIndicator
                  style={{ position: 'absolute', zIndex: 2 }}
                  size={32}
                />
              )}
              {uploadedFiles.length > 0 && (
                <StyledImagePreview
                  resizeMode='cover'
                  source={{
                    uri: selectedImageFiles[0]?.url || undefined,
                    headers: {
                      [api.environment.accessTokenKey]: `Bearer ${accessToken}`
                    }
                  }}
                  onLoadStart={() => {
                    setLoadingImagePreview(true);
                  }}
                  onLoad={() => {
                    setLoadingImagePreview(false);
                  }}
                />
              )}
            </StyledImageUploadArea>
          ) : (
            <>
              {(!uploadedFiles || uploadedFiles?.length <= 0) && (
                <StyledFileArea>
                  {fileInputType !== FileInputType.Image &&
                    fileInputType !== FileInputType.Video && (
                      <StyledButton outlined onPress={selectFile}>
                        Select a file
                      </StyledButton>
                    )}
                  {fileInputType !== FileInputType.Others && (
                    <>
                      {fileInputType !== FileInputType.Image &&
                        fileInputType !== FileInputType.Video && (
                          <>
                            <Spacer />
                            <StyledButtonSeparatorText color='dark'>
                              -- or --
                            </StyledButtonSeparatorText>
                          </>
                        )}
                      <StyledButton
                        outlined
                        onPress={() => {
                          setOpenImageActionSheet(true);
                        }}
                      >
                        {`Upload ${
                          fileInputType === FileInputType.Video
                            ? 'a video'
                            : 'a photo'
                        }`}
                      </StyledButton>
                    </>
                  )}
                </StyledFileArea>
              )}
              {uploadedFiles?.length > 0 && (
                <StyledFilesContainer>
                  {uploadedFileList}
                  {multiple && (
                    <>
                      <Spacer />
                      <StyledMultipleFilesButtonContainer>
                        {fileInputType !== FileInputType.Image &&
                          fileInputType !== FileInputType.Video && (
                            <StyledButton
                              outlined
                              size='small'
                              disabled={isUploadingFiles}
                              onPress={selectFile}
                            >
                              + Add File
                            </StyledButton>
                          )}
                        {fileInputType !== FileInputType.Others && (
                          <>
                            <View style={{ width: 20 }} />
                            <StyledButton
                              outlined
                              size='small'
                              disabled={isUploadingFiles}
                              onPress={() => {
                                setOpenImageActionSheet(true);
                              }}
                            >
                              + Add Image
                            </StyledButton>
                          </>
                        )}
                      </StyledMultipleFilesButtonContainer>
                    </>
                  )}
                </StyledFilesContainer>
              )}
            </>
          )}
          <ImageActionSheet
            visible={openImageActionSheet}
            onClose={() => setOpenImageActionSheet(false)}
            onSelectImage={result => {
              if (!result?.canceled) setOpenImageActionSheet(false);
              handleImageResult(result);
            }}
            selectText={`Take a new ${
              fileInputType === FileInputType.Video ? 'video' : 'image'
            }`}
            captureText={`Choose an existing ${
              fileInputType === FileInputType.Video ? 'video' : 'image'
            }`}
            pickerOptions={{
              mediaTypes:
                fileInputType === FileInputType.Video
                  ? ImagePicker.MediaTypeOptions.Videos
                  : ImagePicker.MediaTypeOptions.Images,
              allowsEditing: false,
              quality: 1
            }}
          />
        </>
      )}
    </View>
  );
};

export const FormFilePlugin: FormElementPlugin<any, FormFileConfig> = {
  name: 'File',
  type: 'File',
  element: FormFile
};
